<template>
    <v-form ref="form" v-model="isValid" class="attribute-filter">
        <vuex-select
            class="attribute-filter-values"
            item-value="attributeKey"
            item-text="attributeName"
            autocomplete
            value-type="object"
            :getter="getAttribute"
            :setter="value => setAttribute(value)"
            :options="attributeOptions"
            :validations="validateAttributeSelect ? validations : []"
            prioritize-exact-match
        />
        <vuex-select
            class="attribute-filter-values"
            autocomplete
            chips
            multiple
            :disabled="!atributeValuesOptions.length"
            :getter="() => getAttributeValues()"
            :setter="setAttributeValues"
            :options="atributeValuesOptions"
            :validations="validations"
            :sort-function="naturalSortOptions"
            prioritize-exact-match
            return-object
        />
        <div class="pt-1">
            <spinner-dynamic v-if="isLoading" />
            <v-btn
                v-else
                :disabled="isReadOnly || removeBtnDisabled"
                icon
                @click="onRemoveAttributeRow"
            >
                <icon icon-name="cancel" small icon-colour-type="primary-path" />
            </v-btn>
        </div>
    </v-form>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import validators from '@/js/validators';
import { naturalSort } from '@/js/utils/sort-utils';

export default {
    props: {
        attributeRowIndex: {
            type: Number,
            required: true,
        },
        attributeScope: {
            type: Array,
            required: true,
        },
        validateAttributeSelect: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            atributeValuesOptions: [],
            isValid: true,
            isLoading: false,
            validations: [
                {
                    validator: validators.required,
                },
            ],
        };
    },
    computed: {
        ...mapGetters('attributeMetadata', [
            'getAvailableAttributes',
            'getAvailableAttributesKeyedByAttributeKey',
        ]),
        attributeRow() {
            return this.attributeScope[this.attributeRowIndex];
        },
        rowAttrubuteKey() {
            return this.attributeRow.attributeKey || null;
        },
        filteredAttribureOptions() {
            return this.getAvailableAttributes.filter(
                item =>
                    item.attributeKey === this.rowAttrubuteKey ||
                    !this.attributeScope.find(
                        scopedItem => scopedItem.attributeKey === item.attributeKey
                    )
            );
        },
        attributeOptions() {
            return this.filteredAttribureOptions.map(attribute => ({
                attributeKey: attribute.attributeKey,
                attributeName: attribute.attributeName,
            }));
        },
        attributeValuesOptionsByAttributeKey() {
            return this.filteredAttribureOptions.reduce((acc, option) => {
                acc[option.attributeKey] = option.attributeValues;
                return acc;
            }, {});
        },
        removeBtnDisabled() {
            return (
                !this.validateAttributeSelect &&
                !this.attributeRowIndex &&
                !this.attributeRow.attributeKey
            );
        },
    },
    mounted() {
        const attr = this.getAttribute();
        if (attr) {
            this.atributeValuesOptions = this.attributeValuesOptionsByAttributeKey[
                attr.attributeKey
            ];
        }
        this.$refs.form.validate();
    },
    methods: {
        ...mapActions('products', ['fetchAvailableValuesForProductAttribute']),
        naturalSortOptions(options) {
            return [...options].sort((a, b) => naturalSort(String(a), String(b)));
        },
        getAttribute() {
            if (!this.attributeRow.attributeKey) {
                return null;
            }

            return {
                attributeKey: this.attributeRow.attributeKey,
                attributeName: this.attributeRow.attributeName,
            };
        },
        getAttributeValues() {
            return this.attributeRow.values;
        },
        setAttributeValues(values) {
            Vue.set(this.attributeScope[this.attributeRowIndex], 'values', values);
            this.$emit('products-number-updated');
        },
        async setAttribute(value) {
            if (value === this.attributeRow.attributeKey) {
                return;
            }
            this.isLoading = true;
            this.atributeValuesOptions = [];
            const fullAttributeObject = this.getAvailableAttributesKeyedByAttributeKey[value];
            Vue.set(this.attributeScope, this.attributeRowIndex, {
                attributeKey: fullAttributeObject ? fullAttributeObject.attributeKey : null,
                attributeName: fullAttributeObject ? fullAttributeObject.attributeName : null,
                key: this.attributeRow.key,
                values: [],
            });
            if (value) {
                this.atributeValuesOptions = this.attributeValuesOptionsByAttributeKey[value] || [];
                this.$nextTick(() => {
                    this.$refs.form.validate();
                });
            } else {
                this.$nextTick(() => {
                    this.$refs.form.resetValidation();
                });
            }
            this.$emit('products-number-updated');

            this.isLoading = false;
        },
        onRemoveAttributeRow() {
            this.atributeValuesOptions = [];
            this.$emit('remove-attribute-row', {
                index: this.attributeRowIndex,
                key: this.attributeRow.key,
            });
        },
    },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

.attribute-filter {
    display: flex;
    flex-direction: row;
    gap: 1rem;

    .rtls-select-container {
        width: 30%;
    }

    .attribute-filter-values {
        ::v-deep {
            .v-input__control {
                min-height: 3.2rem;
            }

            .v-select__slot {
                height: auto !important;
                min-height: 2.2rem;
                min-width: 25rem;
            }

            .v-input--is-disabled {
                .v-text-field__details {
                    display: none;
                }
            }
        }
    }
}
</style>
